import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Outlet, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { AdminGuard } from '@/components/guards/AdminGuard';
import { AdminLayout } from '@/components/layout/AdminLayout';

import { AdminLoginPage } from '@/features/auth/pages/AdminLoginPage';
import { AdminDashboardPage } from '@/features/dashboard/pages/AdminDashboardPage';
import { UsersListPage } from '@/features/users/pages/UsersListPage';
import { UserDetailPage } from '@/features/users/pages/UserDetailPage';
import { OrganisationsListPage } from '@/features/organisations/pages/OrganisationsListPage';
import { OrganisationDetailPage } from '@/features/organisations/pages/OrganisationDetailPage';
import { DomainsListPage } from '@/features/domains/pages/DomainsListPage';
import { DomainDetailPage } from '@/features/domains/pages/DomainDetailPage';
import { AutomationsMonitorPage } from '@/features/automations/pages/AutomationsMonitorPage';
import { ExecutionDetailPage } from '@/features/automations/pages/ExecutionDetailPage';
import { AutomationQueuesPage } from '@/features/automations/pages/AutomationQueuesPage';
import { VaultsOverviewPage } from '@/features/vault-admin/pages/VaultsOverviewPage';
import { VaultDetailPage } from '@/features/vault-admin/pages/VaultDetailPage';
import { BillingOverviewPage } from '@/features/billing/pages/BillingOverviewPage';
import { InvoicesListPage } from '@/features/billing/pages/InvoicesListPage';
import { PricingConfigPage } from '@/features/billing/pages/PricingConfigPage';
import { RevenueAnalyticsPage } from '@/features/billing/pages/RevenueAnalyticsPage';
import { CommunicationsPage } from '@/features/communications/pages/CommunicationsPage';
import { IncidentReportPage } from '@/features/communications/pages/IncidentReportPage';
import { MaintenanceSchedulePage } from '@/features/communications/pages/MaintenanceSchedulePage';
import { AnnouncementsPage } from '@/features/communications/pages/AnnouncementsPage';
import { EmailCampaignsPage } from '@/features/communications/pages/EmailCampaignsPage';
import { SupportTicketsPage } from '@/features/support/pages/SupportTicketsPage';
import { SupportTicketDetailPage } from '@/features/support/pages/SupportTicketDetailPage';
import { SystemHealthPage } from '@/features/monitoring/pages/SystemHealthPage';
import { LogsExplorerPage } from '@/features/monitoring/pages/LogsExplorerPage';
import { BrowserPoolPage } from '@/features/monitoring/pages/BrowserPoolPage';
import { PlatformSettingsPage } from '@/features/settings/pages/PlatformSettingsPage';
import { AuditLogPage } from '@/features/settings/pages/AuditLogPage';

import { useTheme } from '@/hooks/useTheme';
import '@/styles/globals.css';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000,
    },
  },
});

function AdminLayoutWrapper() {
  return (
    <AdminLayout>
      <Outlet />
    </AdminLayout>
  );
}

function ThemeInitializer({ children }: { children: React.ReactNode }) {
  useTheme();
  return <>{children}</>;
}

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
      <ThemeInitializer>
        <Routes>
          {/* Public */}
          <Route path="/login" element={<AdminLoginPage />} />

          {/* Protected */}
          <Route element={<AdminGuard />}>
            <Route element={<AdminLayoutWrapper />}>
              <Route index element={<AdminDashboardPage />} />

              {/* Gestion */}
              <Route path="users" element={<UsersListPage />} />
              <Route path="users/:uuid" element={<UserDetailPage />} />
              <Route path="organisations" element={<OrganisationsListPage />} />
              <Route path="organisations/:uuid" element={<OrganisationDetailPage />} />
              <Route path="domains" element={<DomainsListPage />} />
              <Route path="domains/:uuid" element={<DomainDetailPage />} />

              {/* Operations */}
              <Route path="automations" element={<AutomationsMonitorPage />} />
              <Route path="automations/executions/:uuid" element={<ExecutionDetailPage />} />
              <Route path="automations/queues" element={<AutomationQueuesPage />} />
              <Route path="vaults" element={<VaultsOverviewPage />} />
              <Route path="vaults/:uuid" element={<VaultDetailPage />} />

              {/* Finances */}
              <Route path="billing" element={<BillingOverviewPage />} />
              <Route path="billing/invoices" element={<InvoicesListPage />} />
              <Route path="billing/pricing" element={<PricingConfigPage />} />
              <Route path="billing/revenue" element={<RevenueAnalyticsPage />} />

              {/* Communication */}
              <Route path="communications" element={<CommunicationsPage />} />
              <Route path="communications/incidents" element={<IncidentReportPage />} />
              <Route path="communications/maintenance" element={<MaintenanceSchedulePage />} />
              <Route path="communications/announcements" element={<AnnouncementsPage />} />
              <Route path="communications/emails" element={<EmailCampaignsPage />} />
              <Route path="support" element={<SupportTicketsPage />} />
              <Route path="support/:uuid" element={<SupportTicketDetailPage />} />

              {/* Technique */}
              <Route path="monitoring" element={<SystemHealthPage />} />
              <Route path="monitoring/logs" element={<LogsExplorerPage />} />
              <Route path="monitoring/browser-pool" element={<BrowserPoolPage />} />

              {/* Configuration */}
              <Route path="settings" element={<PlatformSettingsPage />} />
              <Route path="settings/audit-log" element={<AuditLogPage />} />
              <Route path="profile" element={<PlatformSettingsPage />} />
            </Route>
          </Route>
        </Routes>
        <Toaster
          position="top-right"
          expand
          richColors
          closeButton
          duration={4000}
        />
      </ThemeInitializer>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
